import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  createActivityDriverMapping,
  fetchActivitiesList,
  fetchActivityDriverMappings,
  fetchDrivers,
  updateActivityDriverMapping,
} from './activity.apis';
import {
  IActivityDriverForm,
  IActivityDriverItem,
  IActivityListItem,
} from './activity.models';

type Props = {
  navigate?: (path: string) => void;
  activityix?: string;
};

type State = {
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;

  activities: IActivityListItem[];
  drivers: Array<{ recix: string; recname: string; shortcode?: string | null }>;

  selectedActivityix: string;
  mappings: IActivityDriverItem[];

  dialogOpen: boolean;
  dialogMode: 'add' | 'edit';
  form: IActivityDriverForm;
};

export default class ActivityDriverMappingPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      saving: false,
      error: '',
      success: '',

      activities: [],
      drivers: [],

      selectedActivityix: props.activityix || '',
      mappings: [],

      dialogOpen: false,
      dialogMode: 'add',
      form: this.getEmptyForm(props.activityix || ''),
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private getEmptyForm = (activityix: string): IActivityDriverForm => {
    return {
      activityix,
      driverix: '',
      formula: '',
      defaultvalue: '',
      description: '',
      status: 1,
    };
  };

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [activitiesResult, drivers] = await Promise.all([
        fetchActivitiesList({
          search: '',
          typeix: '',
          costcenterix: '',
          status: '1',
          page: 1,
          pagesize: 1000,
        }),
        fetchDrivers(),
      ]);

      const activities = activitiesResult.items || [];
      const selectedActivityix =
        this.state.selectedActivityix || activities[0]?.recix || '';

      this.setState(
        {
          activities,
          drivers,
          selectedActivityix,
          form: this.getEmptyForm(selectedActivityix),
        },
        async () => {
          if (selectedActivityix) {
            await this.loadMappings(selectedActivityix);
          } else {
            this.setState({ loading: false });
          }
        }
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity driver mapping page',
      });
    }
  };

  private loadMappings = async (activityix: string): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      const result = await fetchActivityDriverMappings(activityix);

      this.setState({
        mappings: result.items || [],
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity driver mappings',
      });
    }
  };

  private handleActivityChange = (event: SelectChangeEvent<string>): void => {
    const selectedActivityix = event.target.value;

    this.setState(
      {
        selectedActivityix,
        form: this.getEmptyForm(selectedActivityix),
      },
      () => {
        if (selectedActivityix) {
          this.loadMappings(selectedActivityix);
        }
      }
    );
  };

  private openAddDialog = (): void => {
    this.setState({
      dialogOpen: true,
      dialogMode: 'add',
      form: this.getEmptyForm(this.state.selectedActivityix),
      error: '',
      success: '',
    });
  };

  private openEditDialog = (mapping: IActivityDriverItem): void => {
    this.setState({
      dialogOpen: true,
      dialogMode: 'edit',
      form: {
        recix: mapping.recix,
        activityix: this.state.selectedActivityix,
        driverix: mapping.driver?.recix || '',
        formula: mapping.formula || '',
        defaultvalue: mapping.defaultvalue ?? '',
        description: mapping.description || '',
        status: mapping.status ?? 1,
      },
      error: '',
      success: '',
    });
  };

  private closeDialog = (): void => {
    this.setState({
      dialogOpen: false,
      form: this.getEmptyForm(this.state.selectedActivityix),
    });
  };

  private handleTextChange =
    (field: keyof IActivityDriverForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: value,
        },
      }));
    };

  private handleNumberChange =
    (field: keyof IActivityDriverForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const rawValue = event.target.value;
      const value = rawValue === '' ? '' : Number(rawValue);

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: value,
        },
      }));
    };

  private handleSelectChange =
    (field: keyof IActivityDriverForm) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: field === 'status' ? Number(value) : value,
        },
      }));
    };

  private validateForm = (): string[] => {
    const { form } = this.state;
    const errors: string[] = [];

    if (!form.activityix) errors.push('Activity is required.');
    if (!form.driverix) errors.push('Driver is required.');
    if (form.defaultvalue === '' || Number(form.defaultvalue) < 0) {
      errors.push('Default value is required.');
    }

    return errors;
  };

  private handleSave = async (): Promise<void> => {
    const errors = this.validateForm();

    if (errors.length > 0) {
      this.setState({
        error: errors.join(' '),
        success: '',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      if (this.state.dialogMode === 'add') {
        await createActivityDriverMapping(this.state.form);
      } else if (this.state.form.recix) {
        await updateActivityDriverMapping(this.state.form.recix, this.state.form);
      }

      this.setState({
        saving: false,
        dialogOpen: false,
        success: 'Activity driver mapping saved successfully.',
        form: this.getEmptyForm(this.state.selectedActivityix),
      });

      if (this.state.selectedActivityix) {
        await this.loadMappings(this.state.selectedActivityix);
      }
    } catch (error: any) {
      this.setState({
        saving: false,
        error: error?.message || 'Failed to save activity driver mapping',
      });
    }
  };

  private handleBack = (): void => {
    const path = '/forms/activities';

    if (this.props.navigate) {
      this.props.navigate(path);
      return;
    }

    window.location.href = path;
  };

  private handleRefresh = (): void => {
    if (this.state.selectedActivityix) {
      this.loadMappings(this.state.selectedActivityix);
    }
  };

  render(): React.ReactNode {
    const {
      loading,
      saving,
      error,
      success,
      activities,
      drivers,
      selectedActivityix,
      mappings,
      dialogOpen,
      dialogMode,
      form,
    } = this.state;

    const selectedActivity =
      activities.find((x) => x.recix === selectedActivityix) || null;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Activity Driver Mapping
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Map workload or cost drivers to ABB activities.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={this.handleBack}
            >
              Back
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={this.handleRefresh}
              disabled={!selectedActivityix}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.openAddDialog}
              disabled={!selectedActivityix}
            >
              Add Driver
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        ) : null}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-driver-activity-label">
                    Activity
                  </InputLabel>
                  <Select
                    labelId="activity-driver-activity-label"
                    value={selectedActivityix}
                    label="Activity"
                    onChange={this.handleActivityChange}
                  >
                    {activities.map((activity) => (
                      <MenuItem key={activity.recix} value={activity.recix}>
                        {activity.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>Selected:</strong>{' '}
                    {selectedActivity?.recname || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Code:</strong> {selectedActivity?.shortcode || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cost Center:</strong>{' '}
                    {selectedActivity?.costcenter?.recname || '-'}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Stack spacing={2}>
          {loading ? (
            <Card>
              <CardContent>
                <Typography>Loading mappings...</Typography>
              </CardContent>
            </Card>
          ) : mappings.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="text.secondary">
                  No drivers mapped to this activity yet.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            mappings.map((mapping, index) => (
              <Card key={mapping.recix || index}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {mapping.driver?.recname || 'Unnamed Driver'}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
                        {mapping.driver?.shortcode ? (
                          <Chip
                            size="small"
                            label={`Code: ${mapping.driver.shortcode}`}
                          />
                        ) : null}
                        <Chip
                          size="small"
                          color={mapping.status === 1 ? 'success' : 'default'}
                          label={mapping.status === 1 ? 'Active' : 'Disabled'}
                        />
                      </Stack>

                      <Typography variant="body2">
                        <strong>Formula:</strong> {mapping.formula || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Default Value:</strong> {mapping.defaultvalue}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Description:</strong> {mapping.description || '-'}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => this.openEditDialog(mapping)}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>

        <Dialog
          open={dialogOpen}
          onClose={this.closeDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {dialogMode === 'add'
              ? 'Add Activity Driver Mapping'
              : 'Edit Activity Driver Mapping'}
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="mapping-driver-label">Driver</InputLabel>
                  <Select
                    labelId="mapping-driver-label"
                    value={form.driverix}
                    label="Driver"
                    onChange={this.handleSelectChange('driverix')}
                  >
                    {drivers.map((driver) => (
                      <MenuItem key={driver.recix} value={driver.recix}>
                        {driver.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Default Value"
                  type="number"
                  value={form.defaultvalue}
                  onChange={this.handleNumberChange('defaultvalue')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="mapping-status-label">Status</InputLabel>
                  <Select
                    labelId="mapping-status-label"
                    value={`${form.status}`}
                    label="Status"
                    onChange={this.handleSelectChange('status')}
                  >
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Formula"
                  value={form.formula}
                  onChange={this.handleTextChange('formula')}
                  placeholder="e.g. dispatch_count * avg_cost_per_dispatch"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  minRows={3}
                  value={form.description}
                  onChange={this.handleTextChange('description')}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={this.closeDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={this.handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}
